import { AfterViewInit, Component, Input, OnDestroy, ViewChild, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { IonModal, ModalController, ToastController } from '@ionic/angular';
import { CustomFieldType, UpsertWorkoutStep, measurementSystemsWeightMapping } from '@me-fit-mono/typings';
import { TranslateService } from '@ngx-translate/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridReadyEvent, ICellRendererParams, IRichCellEditorParams, RowDragEvent } from 'ag-grid-community';
import { isEmpty, isNil, noop } from 'lodash-es';
import { ME_Fit } from 'me-fit-typings';
import { filter, firstValueFrom, map, merge } from 'rxjs';
import { ConfirmAlert } from 'src/app/decorators/confirm-alert-decorator';
import { Feedback } from 'src/app/decorators/feedback-decorator';
import { CustomersStateService } from 'src/app/state/customers.state.service';
import { OrganizationStateService } from 'src/app/state/organization.state.service';
import { WorkoutStepData } from 'src/app/state/workout.state.service';
import { PreferredMeasurementService } from '../../services/preferred-measurement.service';
import { PersonalRecordsSelection, PersonalRecordsSelectionModalComponent } from '../modals/personal-records-selection-modal/personal-records-selection-modal.component';
import { WorkoutStepDisplayPipe } from '../pipes/workout-step-display.pipe';
import { AgGridButtonRendererComponent } from './ag-grid-button-renderer/ag-grid-button-renderer.component';
import { AgGridNumberEditorComponent, AgGridNumberEditorParams } from './ag-grid-number-editor/ag-grid-number-editor.component';
import { AgGridValueEditorComponent, AgGridValueEditorParams } from './ag-grid-value-editor/ag-grid-value-editor.component';
import { AgGridExerciseCellEditorParams, ExerciseCellRendererComponent } from './exercise-cell-renderer/exercise-cell-renderer.component';
import { ExerciseCellSearchComponent } from './exercise-cell-search/exercise-cell-search.component';
import { ExerciseRendererService } from './exercise-renderer.service';
import { ExerciseSearchComponent } from './exercise-search/exercise-search.component';
import { takeUntil, tap } from 'rxjs/operators';
import { Subject, combineLatest } from 'rxjs';
import { AlgoliaSdkType } from 'src/app/services/algolia.service';
import { ExerciseFilterModalComponent } from '../modals/exercise-filter-modal/exercise-filter-modal.component';

interface WorkoutStepFormType {
  exerciseId: FormControl<string|undefined>;
  type: FormControl<ME_Fit.WorkoutStepType>;
  sets: FormControl<number>;
  value: FormControl<number|undefined>;
  weightValue: FormControl<number|null>;
  weightRequired: FormControl<boolean|null>;
  break: FormControl<number|null>;
  notes: FormControl<string|undefined>;
  superSet: FormControl<string|null>;
  customFields: FormGroup<{ [key: string]: any }>;
}


@Component({
  selector: 'app-workout',
  templateUrl: './workout.component.html',
  styleUrls: ['./workout.component.scss'],
  providers: [WorkoutStepDisplayPipe]
})
export class WorkoutComponent implements AfterViewInit, OnDestroy {
  @ViewChild('agGrid', { static: false }) agGrid: AgGridAngular<WorkoutStepData>;

  @Input({required: false}) customerId?: string|null;

  @Input() anonymous = false;

  exerciseSearchModal: HTMLIonModalElement;

  defaultColDef: ColDef = {
    resizable: true,
    menuTabs: []
  }

  /** This will be used on mobile */
  workoutStepForm = new FormGroup<WorkoutStepFormType>({
    exerciseId: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    type: new FormControl('repetitions', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    sets: new FormControl(0, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    value: new FormControl(undefined, {
      nonNullable: true
    }),
    weightValue: new FormControl(),
    weightRequired: new FormControl(),
    break: new FormControl(),
    notes: new FormControl(),
    superSet: new FormControl(),
    customFields: new FormGroup({})
  });

  get workoutStepFormCustomFields(): FormGroup {
    return this.workoutStepForm.get('customFields') as FormGroup;
  }

  @Input() enableRearrangement = true;

  @Input()
  editingWorkout: boolean = false;

  @ViewChild('workoutStepFormModal') workoutStepFormModal: IonModal;

  @ViewChild(ExerciseSearchComponent) exerciseSearchComponent: ExerciseSearchComponent

  /** Represents workout step we are currently editing in the modal, we need this to be able to update the exercise after the form is submitted */
  currentWorkoutStepInModal: WorkoutStepData;

  possisbleWorkoutTypes: Array<{
    value: ME_Fit.WorkoutStepType,
    label: string
  }> = [
    {
      value: 'interval',
      label: this.$translate.instant('duration')
    },
    {
      value: 'repetitions',
      label: this.$translate.instant('repetition.plural')
    }
  ];

  possibleSuperSets = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

  weightUnitOfMeasure$ = this.$preferredMeasurementService.weightUnitOfMeasure$;

  @Output() workoutStepsChanged = new EventEmitter<UpsertWorkoutStep[]>();

  get emptyNumericValueToUse(): AgGridNumberEditorParams['emptyValueToUseCallback'] {
    /**
     * When we are editing we want the value to be null, as we want to be able to clear the value
     * undefined does not end up in the JSON object, so we can't clear the value
     */
    return () => this.editingWorkout ? null : undefined;
  }

  get algoliaSdkType(): AlgoliaSdkType {
    return this.anonymous ? 'anonymous' : 'global';
  }

  columnDefs: Array<ColDef> = [
    {
      field: 'id',
      suppressColumnsToolPanel: true,
      hide: true,
      editable: false
    },
    {
      field: 'drag-column',
      headerName: '',
      rowDrag: true,
      editable: false,
      width: 30,
      rowDragText: (params, dragCount) => {
        console.log(
          `[WorkoutComponent:getValue] `,
          params
        );

        return this.$exerciseRenderer.getLocalExercise(params.defaultTextValue)?.name ?? '';
      },
    },
    {
      field: 'exerciseId',
      headerName: this.$translate.instant('exercise.singular'),
      editable: true,
      flex: 1,
      minWidth: 230,
      cellEditor: 'exerciseCell',
      cellRenderer: 'exerciseRenderer',
      cellEditorParams: {
        algoliaSdkType: 'global'
      } satisfies AgGridExerciseCellEditorParams
    },
    {
      field: 'type',
      headerName: this.$translate.instant('workout.step.type'),
      cellEditor: 'agSelectCellEditor',
      editable: true,
      flex: 1,
      singleClickEdit: true,
      cellEditorParams: {
        values: this.possisbleWorkoutTypes.map(possibleType => possibleType.value)
      }
    },
    {
      field: 'sets',
      headerName: this.$translate.instant('sets.singular'),
      editable: true,
      flex: 1,
      cellEditor: 'agGridNumberEditorComponent',
      cellEditorParams: {
        defaultValue: 1,
        allowDecimals: false,
        emptyValueToUseCallback: () => {
          // Sets is non nullable
          return 0;
        }
      } satisfies AgGridNumberEditorParams
    },
    {
      colId: 'value',
      field: 'value',
      headerName: this.$translate.instant('workout.step.value'),
      editable: true,
      flex: 1,
      cellEditor: 'valueInputCell',
      cellEditorPopup: true,
      cellEditorPopupPosition: 'over',
      valueFormatter: (e) => {
        const exerciseRowData = e.data as WorkoutStepData;

        return this.workoutStepDisplayPipe.getLabelForValue({
          type: exerciseRowData.type,
          value: exerciseRowData.value
        })
      },
      cellEditorParams: {
        defaultValue: 0,
      } as AgGridValueEditorParams
    },
    {
      field: 'weightValue',
      // TODO: locale calculation, figure out how users will input pounds and convert it to kg's under the hood.
      //
      headerName: '',
      editable: true,
      flex: 1,
      cellEditor: 'agGridNumberEditorComponent',
      cellEditorParams: {
        allowDecimals: true,
        emptyValueToUseCallback: this.emptyNumericValueToUse
      } satisfies AgGridNumberEditorParams
    },
    {
      field: 'weightRequired',
      headerName: this.$translate.instant('weight.required'),
      headerTooltip: this.$translate.instant('weight.required.message'),
      editable: true,
      flex: 1,
      cellEditor: 'agCheckboxCellEditor',
      sortable: false,
      singleClickEdit: true,

    },
    {
      field: 'break',
      headerName: this.$translate.instant('workout.step.break'),
      editable: true,
      flex: 1,
      cellEditor: 'agGridNumberEditorComponent',
      valueFormatter: (e) => (!isNil(e.value) ? `${e.value}s` : ''),
      cellEditorParams: {
        defaultValue: 0,
        allowDecimals: false,
        emptyValueToUseCallback: () => {
          // Break is a non nullable field.
          return 0;
        }
      } satisfies AgGridNumberEditorParams
    },
    {
      field: 'superSet',
      headerName: this.$translate.instant('super.sets'),
      editable: true,
      flex: 1,
      cellEditor: 'agRichSelectCellEditor',
      singleClickEdit: true,
      cellEditorParams: {
        values: ['', ...this.possibleSuperSets],
        allowTyping: true,
      } satisfies Partial<IRichCellEditorParams>
    },
    {
      field: 'notes',
      headerName: this.$translate.instant('notes'),
      editable: true,
      flex: 1,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorParams: {
        maxLength: 100,
        rows: 1,
      }
    },
    {
      field: 'action',
      headerName: '',
      editable: false,
      cellRenderer: 'agGridButtonRendererComponent',
      width: 50,
      cellStyle: {
        padding: 0,
      },
      cellRendererParams: {
        onClick: (e: ICellRendererParams) => {
          this.deleteWorkoutStep(e.rowIndex);
        },
        buttonColor: 'danger',
        buttonIcon: 'remove-outline',
      }
    }
  ];

  workoutSteps: WorkoutStepData[] = [];

  searchExerciseForm = this.formBuilder.group({
    search: []
  });

  components = {
    exerciseCell: ExerciseCellSearchComponent,
    exerciseRenderer: ExerciseCellRendererComponent,
    agGridNumberEditorComponent: AgGridNumberEditorComponent,
    agGridButtonRendererComponent: AgGridButtonRendererComponent,
    valueInputCell: AgGridValueEditorComponent
  };

  workoutCustomFields$ = this.$organization.getCustomFields.getResponse$().pipe(
    filter(response => !isNil(response)),
    map(response => {
      return (response?.workoutStepCustomFields ?? [])
        .filter( customField => !customField.isArchived)
    })
  );

  customFieldsLoading$ = this.$organization.getCustomFields.isLoading$();

  stop$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private $translate: TranslateService,
    private $exerciseRenderer: ExerciseRendererService,
    private workoutStepDisplayPipe: WorkoutStepDisplayPipe,
    private toastCtrl: ToastController,
    private $organization: OrganizationStateService,
    private $preferredMeasurementService: PreferredMeasurementService,
    private $customer: CustomersStateService,
    private modalCtrl: ModalController
  ) { }

  ngAfterViewInit(): void {
    // Needed to make sure popups are not clipped by the small grid
    this.agGrid.api.setGridOption('popupParent', document.body);

    this.resetWorkoutStepForm();

    if (!this.enableRearrangement) {
      this.agGrid.columnApi.setColumnVisible('drag-column', false);
    }

    if (!this.anonymous) {
      this.setWorkoutStepsCustomFields();
    }

    if (this.anonymous) {
      // hide weight required field
      //
      this.columnDefs = this.columnDefs.map(column => {
        if (column.field === 'weightRequired') {
          return {
            ...column,
            hide: true
          };
        }

        if (column.field === 'exerciseId') {
          return {
            ...column,
            cellEditorParams: {
               algoliaSdkType: 'anonymous'
            } satisfies AgGridExerciseCellEditorParams
          }
        }
        return column;
      });
    }

    // Set the weightValue column based on the preferred measurement service
    this.setWeightValueColumnDef();

    // Syncing the value with the parent
    // This is triggered whenever cell values are changed
    this.agGrid.cellValueChanged.pipe(
      tap(() => console.log('[workout.component] cellValueChanged')),
      takeUntil(this.stop$)
    ).subscribe(() => this.onWorkoutChange());

    // Syncing the value with the parent
    // This is triggered whenever rows are added, re-sorted or removed
    this.agGrid.rowDataUpdated.pipe(
      tap(() => console.log('[workout.component]  rowDataUpdated')),
      takeUntil(this.stop$)
    ).subscribe(() => this.onWorkoutChange());
  }

  onWorkoutChange() {
    const value = this.getValue();

    this.workoutStepsChanged.emit(value);
  }

  onGridReady(params: GridReadyEvent) {


  }

  private async setWeightValueColumnDef() {
    let weightUnitOfMeasure: string;
    if (this.anonymous) {
      // We have no easy way of figuring out what the user preference is, in the future we will rely on a browser locale approach
      // and let users choose.
      weightUnitOfMeasure = measurementSystemsWeightMapping['metric'];
    } else {
      weightUnitOfMeasure = await firstValueFrom(this.weightUnitOfMeasure$);
    }


    // Update the column defs to include the weight unit of measure
    this.columnDefs = this.columnDefs.map(column => {
      if (column.field !== 'weightValue') {
        return column;
      }

      return {
        ...column,
        headerName: this.$translate.instant('workout.step.weight', {
          unitOfMeasure: weightUnitOfMeasure
        }),
        valueFormatter: (e) => (!isNil(e.value) ? `${e.value} ${weightUnitOfMeasure}` : ''),
      };
    })

    this.agGrid.api.setGridOption('columnDefs', this.columnDefs);
  }

  async setWorkoutStepsCustomFields() {
    this.agGrid.api.showLoadingOverlay()
    try {
      const workoutStepCustomFields = await firstValueFrom(this.workoutCustomFields$);

      if (isEmpty(workoutStepCustomFields)) {
        return;
      }

      const fieldMapping: { [key in CustomFieldType]: string} = {
        number: 'agGridNumberEditorComponent',
        text: 'agLargeTextCellEditor'
      }

      const fieldParams: { [key in CustomFieldType]: Object } = {
        number: {
          allowDecimals: true,
          // Ideally we would want to use undefined here, but the current implementation does not support this yet
          emptyValueToUseCallback: () => this.editingWorkout ? 0 : undefined
        } satisfies AgGridNumberEditorParams,
        text: {}
      }


      const customFieldsColDefinitions = workoutStepCustomFields.map(customField => {
        return {
          field: customField.id,
          headerName: customField.name,
          editable: true,
          flex: 1,
          cellEditor: fieldMapping[customField.type],
          cellEditorParams: {
            isCustomField: true,
            ...fieldParams[customField.type]
          },
          valueSetter: (params) => {
            if (!params.data.customFields) {
              params.data.customFields = {};
            }

            params.data.customFields[customField.id] = params.newValue;

            return true;
          },
          valueGetter: (params) => {
            return params.data.customFields?.[customField.id] ?? null;
          }
        } satisfies ColDef
      });

      workoutStepCustomFields.forEach(customField => {
        this.workoutStepFormCustomFields.addControl(customField.id, this.formBuilder.control(null));
      });

      // We want to add something before the last element, as the last element is the action column
      const position = this.columnDefs.length - 1;
      this.columnDefs.splice(position, 0, ...customFieldsColDefinitions);

      this.agGrid.api.setGridOption('columnDefs', this.columnDefs);
    } catch {} finally {
      this.agGrid.api.hideOverlay();
      this.agGrid.api.refreshHeader();
    }
  }

  resetWorkoutStepForm() {
    this.columnDefs.forEach(column => {
      if (!column.editable) {
        return;
      }

      if (isNil(column.field)) {
        console.error('[WorkoutComponent:resetWorkoutStepForm] field is missing on this column', column);
        return;
      }

      // Custom fields are somewhere else in the form
      if (column.cellEditorParams?.isCustomField) {
        const customFieldId = column.field;
        this.workoutStepFormCustomFields.get(customFieldId)?.setValue((column.cellEditorParams ?? {}).defaultValue);
        return;
      }

      if (this.workoutStepForm.get(column.field)) {
        this.workoutStepForm.get(column.field)!.setValue((column.cellEditorParams ?? {}).defaultValue);
      } else {
        console.error('[WorkoutComponent:resetWorkoutStepForm] no form control found in workoutStepForm', column.field);
      }
    });

    this.workoutStepForm.reset();
  }

  openWorkoutStepFormModal(workoutStep: WorkoutStepData) {
    this.currentWorkoutStepInModal = workoutStep;

    this.workoutStepForm.patchValue({
      exerciseId: workoutStep.exerciseId,
      type: workoutStep.type,
      sets: workoutStep.sets,
      value: workoutStep.value,
      weightValue: workoutStep.weightValue,
      weightRequired: workoutStep.weightRequired,
      break: workoutStep.break,
      notes: workoutStep.notes,
      superSet: workoutStep.superSet as string|undefined,
      customFields: workoutStep.customFields
    });

    this.workoutStepFormModal.present();
  }

  onWorkoutStepFormSubmit() {
    if (this.workoutStepForm.invalid) {
      return;
    }

    const matchingRowDataEntry = this.workoutSteps.find(rowDataEntry => rowDataEntry === this.currentWorkoutStepInModal);

    if (isNil(matchingRowDataEntry)) {
      console.error('[WorkoutComponent:onWorkoutStepFormSubmit] no matching row data entry found for current workout step in modal');
      return;
    }

    const updatedRowDataEntry = {
      ...matchingRowDataEntry,
      ...this.workoutStepForm.value as WorkoutStepData
    }

    // This will update the workoutSteps with the updated entry
    //
    this.workoutSteps.splice(this.workoutSteps.indexOf(matchingRowDataEntry), 1, updatedRowDataEntry);

    this.agGrid.api.setRowData(this.workoutSteps);

    this.workoutStepFormModal.dismiss();

    this.resetWorkoutStepForm();
  }

  setValue(newWorkoutStep: WorkoutStepData[]) {
    this.workoutSteps = newWorkoutStep;

    this.agGrid.api.setGridOption('rowData', newWorkoutStep);
  }

  async onExercisesSelection(exercises: ME_Fit.Exercise[]) {
    // When creating personal workouts, we want to check a users personal records
    // and prompt the user to add them to the workout
    const selectedRecords = await this.getSelectedPersonalRecords(
      exercises.map(exercise => exercise.id)
    );

    exercises.forEach(exercise => {
      this.workoutSteps.push(
        this.getDefaultNewWorkoutStep(
          exercise.id,
          selectedRecords
        )
      );
      this.agGrid.api.setGridOption('rowData', this.workoutSteps);

      this.agGrid.api.setFocusedCell(this.workoutSteps.length - 1, 'exercise');
    });
  }

  async getSelectedPersonalRecords(exerciseIds: string[]): Promise<PersonalRecordsSelection> {
    if (!this.customerId) {
      return {};
    }

    if (exerciseIds.length === 0) {
      return {};
    }

    const response = await this.fetchPersonalRecords(exerciseIds)
      .catch(noop)

    if (response && !isEmpty(response?.exerciseMap) && response?.exercises) {
      const modal = await this.modalCtrl.create({
        component: PersonalRecordsSelectionModalComponent,
        componentProps: {
          exerciseMap: response.exerciseMap,
          exercises: response.exercises,
          customerId: this.customerId
        }
      });
      await modal.present();
      const { data: selectedRecords } = await modal.onWillDismiss<PersonalRecordsSelection>();
      return selectedRecords || {};
    }

    return {};
  }

  @Feedback({
    i18nFailKey: 'customer.personalRecords.getExercisePerformanceRecords.fail'
  })
  async fetchPersonalRecords(exerciseIds: string[]) {
    if (!this.customerId) {
      throw new Error('Customer ID is required to fetch personal records');
    }

    const response = await this.$customer.getExercisePerformanceRecords.call({
      pathParts: {
        customerId: this.customerId
      },
      request: {
        exerciseIds
      }
    });

    return response;
  }

  async createPartialWorkoutStep() {
    if (isEmpty(this.searchExerciseForm.value.search)) {
      this.toastCtrl.create({
        message: this.$translate.instant('workout.add.exercise.invalid.form.message'),
        duration: 3000,
        buttons: [{
          text: this.$translate.instant('workout.add.exercise.invalid.form.action'),
          handler: () => {
            this.exerciseSearchComponent.openExerciseFilterModal();

            return;
          }
        }]
      }).then(toast => toast.present());

      return;
    }

    const exerciseId = this.searchExerciseForm.value.search;
    if (!exerciseId) {
      return;
    }

    // When creating personal workouts, we want to check a users personal records
    // and prompt the user to add them to the workout
    const selectedRecords = await this.getSelectedPersonalRecords([exerciseId]);

    this.workoutSteps.push(
      this.getDefaultNewWorkoutStep(
        exerciseId,
        selectedRecords
      )
    );

    this.agGrid.api.setGridOption('rowData', this.workoutSteps);

    this.agGrid.api.setFocusedCell(this.workoutSteps.length - 1, 'exercise');

    this.searchExerciseForm.get('search')!.reset();
  }

  getDefaultNewWorkoutStep(exerciseId: string, selectedRecords: PersonalRecordsSelection): WorkoutStepData {
    return {
      exerciseId: exerciseId,
      value: selectedRecords[exerciseId]?.value ?? 10,
      sets: selectedRecords[exerciseId]?.sets ?? 1,
      weightValue: selectedRecords[exerciseId]?.weightValue ?? undefined,
      weightRequired: false,
      break: 30,
      type: 'repetitions',
      notes: undefined,
      customFields: {}
    }
  }

  getValue() {
    this.agGrid.api.stopEditing();

    const workoutStepsData: Array<WorkoutStepData> = [];

    this.agGrid.api.forEachNode((node) => {
      const workoutStepData = node.data!;

      // If super set is an empty string, set it to null
      // so updating the workout step will work
      if (isEmpty(workoutStepData.superSet)) {
        workoutStepData.superSet = null;
      }

      workoutStepsData.push(workoutStepData);
    });


    const upsertWorkoutStep: Array<UpsertWorkoutStep> = workoutStepsData.map(step => ({
      ...step,
      // We don't want to send updatedAt to the backend to ensure it gets updated
      updatedAt: undefined,
      // Transforming custom fields into the correct structure (key-value => array)
      // and filtering out the ones that are empty
      customFields: Object.entries(step.customFields ?? {})
        .map(([workoutStepCustomFieldId, value]) => ({
          workoutStepCustomFieldId,
          value,
        })).filter(customField => !isNil(customField.value))
    }));


    return upsertWorkoutStep;
  }

  @ConfirmAlert({
    i18nTitleKey: 'workout.step.delete.title',
    i18nMessageKey: 'workout.step.delete.message',
    i18nConfirmButtonKey: 'action.remove',
  })
  async deleteWorkoutStep(rowDataEntryIndex: number) {
    this.workoutSteps.splice(rowDataEntryIndex, 1);

    this.agGrid.api.setRowData(this.workoutSteps);
  }

  rowDragEnd(event: RowDragEvent) {
    console.log('[WorkoutComponent:rowDragEnd] event', event);

    const workoutStepsAfterReOrder: WorkoutStepData[] = [];

    this.agGrid.api.forEachNode((node) => {
      workoutStepsAfterReOrder.push(node.data!);
    });

    this.workoutSteps = workoutStepsAfterReOrder;
  }

  ngOnDestroy() {
    this.workoutStepFormModal?.dismiss().catch(noop);
    this.stop$.next();
    this.exerciseSearchModal?.dismiss();
  }

  async openExerciseFilterModal() {
    this.exerciseSearchModal = await this.modalCtrl.create({
      component: ExerciseFilterModalComponent,
      componentProps: {
        anonymous: this.algoliaSdkType === 'anonymous',
      },
      cssClass: 'fullscreen-modal'
    });

    await this.exerciseSearchModal.present();

    const dismissEvent = await this.exerciseSearchModal.onDidDismiss<ME_Fit.Exercise[]>();

    if (dismissEvent.data) {
      this.onExercisesSelection(dismissEvent.data);
    }
  }
}
